import {PageStandard} from "../utils/PageStandard";
import {
    Button,
    ButtonGroup,
    Card,
    Checkbox,
    Chip,
    Drawer,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Sheet,
    Stack,
    Typography
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {UserModel} from "../utils/Model";
import {deleteUser, editUser, getUsers} from "../utils/ViewModel";
import {DeleteForever, Edit, FileDownload, Lock} from "@mui/icons-material";
import {downloadCSV, hasChanges} from "../utils/Utils";
import moment from "moment";


export function Utenti() {

    const [utenti, setUtenti] = useState<UserModel[]>([])
    useEffect(() => {
        loadData()
    }, [])

    function loadData() {
        getUsers().then(data => {
            setUtenti(data);
        });
    }

    return (
        <PageStandard title="Utenti" sidebarVisible={true} headerEnd={
            <Stack direction="row" spacing={2}>
                <Button startDecorator={<FileDownload />}
                        onClick={() => {
                            downloadCSV<UserModel>({
                                data: utenti,
                                fields: ["firstName", "lastName", "email"],
                                filename: `oasi-utenti-${moment().format("YYYY-MM-DD")}.csv`,
                            })
                        }}
                >
                    Esporta tutti in CSV
                </Button>
                <Button startDecorator={<FileDownload />}
                        onClick={() => {
                            downloadCSV<UserModel>({
                                data: utenti,
                                fields: ["firstName", "lastName", "email"],
                                filename: `oasi-utenti-marketing-${moment().format("YYYY-MM-DD")}.csv`,
                                filter: u => u.marketing,
                            })
                        }}
                >
                    Esporta solo marketing in CSV
                </Button>
            </Stack>
        }>
            <Stack direction="column" spacing={2} pb={5}>
                {
                    utenti?.length > 0 ?
                        utenti.map(u => <ItemUser key={u.id} data={u} loadData={loadData} />)
                        :
                        <Typography level="body-md">Non sono presenti utenti</Typography>
                }
            </Stack>
        </PageStandard>
    )
}

function ItemUser(props: {data: UserModel, loadData: () => void}) {
    const defaultData = props.data;
    const loadData = props.loadData;

    const [data, setData] = useState<UserModel>(props.data);
    const [modalEdit, setModalEdit] = useState(false)

    function handleChange<T extends keyof UserModel>(
        key: T,
        value: UserModel[T]
    ) {
        setData((old) => ({...old, [key]: value}));
    }

    const handleSubmit = () => {
        editUser(data)
            .then(() => {
                setModalEdit(false);
                loadData();
            })
    };

    return (
        <Card>
            <Stack direction="row" spacing={2} justifyContent="space-between">
                <Stack direction="column" spacing={0} justifyContent="center">
                    {
                        defaultData.isAdmin ?
                            <Chip size="sm" variant="outlined" color="danger" sx={{fontWeight: "bold"}}>
                                Admin
                            </Chip>
                            : null
                    }
                    <Typography level="body-md" fontWeight="bold">{defaultData.firstName} {defaultData.lastName}</Typography>
                    <Typography level="body-sm">{defaultData.email}</Typography>
                </Stack>
                <Stack direction="column" spacing={1}>
                    <ButtonGroup>
                        <IconButton color="primary" variant="outlined"
                                    sx={{width: "100%"}}
                                    onClick={() => {
                                        setModalEdit(true)
                                    }}
                        >
                            <Edit />
                        </IconButton>
                        <IconButton color="danger" variant="outlined"
                                    sx={{width: "100%"}}
                                    onClick={() => {
                                        if(window.confirm((`Eliminare definitivamente l'utente ${defaultData.email}?`))) {
                                            deleteUser(defaultData.id)
                                                .then(() => loadData())
                                        }
                                    }}
                        >
                            <DeleteForever />
                        </IconButton>
                    </ButtonGroup>
                    <Typography level="body-xs" sx={{fontFamily:"monospace"}}>UID: {defaultData.id}</Typography>
                </Stack>
            </Stack>

            <Drawer open={modalEdit} onClose={() => cancelModal()}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Modifica utente
                        </Typography>

                        <FormControl>
                            <FormLabel>Nome</FormLabel>
                            <Input
                                type="text"
                                value={data.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                                placeholder="Mario"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Cognome</FormLabel>
                            <Input
                                type="text"
                                value={data.lastName}
                                onChange={(e) => handleChange("lastName", e.target.value)}
                                placeholder="Rossi"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={data.email}
                                onChange={() => {}}
                                placeholder="name@email.com"
                                readOnly
                                endDecorator={<Lock />}
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.terms)}
                                onChange={() => handleChange("terms", !Boolean(data.terms))}
                                label="Termini e condizioni"
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.marketing)}
                                onChange={() => handleChange("marketing", !Boolean(data.marketing))}
                                label="Consenso marketing"
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.isAdmin)}
                                onChange={() => handleChange("isAdmin", !Boolean(data.isAdmin))}
                                label="Permessi amministratore"
                            />
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <Button fullWidth
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => cancelModal()}
                            >
                                Annulla
                            </Button>
                            <Button
                                fullWidth
                                disabled={
                                    data.firstName?.trim().length === 0 ||
                                    data.lastName?.trim().length === 0 ||
                                    !hasChanges(defaultData, data)
                                }
                                onClick={handleSubmit}
                            >
                                Modifica
                            </Button>
                        </Stack>

                    </Stack>
                </Sheet>
            </Drawer>
        </Card>
    )

    function cancelModal() {
        setModalEdit(false)
        setData(defaultData)
    }
}